import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

const styles = {
  body: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#333',
    lineHeight: 1.6,
    background: '#f8f9fa',
    minHeight: '100vh',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '40px 24px',
  },
  h1: {
    fontSize: 28,
    fontWeight: 600,
    marginBottom: 8,
    color: '#1a1a1a',
  },
  subtitle: {
    color: '#666',
    marginBottom: 32,
    fontSize: 14,
  },
  h2: {
    fontSize: 18,
    fontWeight: 600,
    marginTop: 28,
    marginBottom: 12,
    color: '#1a1a1a',
  },
  p: {
    fontSize: 15,
    marginBottom: 12,
  },
  keyword: {
    fontWeight: 600,
    fontFamily: "'Roboto Mono', monospace",
    background: '#e9ecef',
    padding: '2px 6px',
    borderRadius: 3,
  },
  link: { color: '#1a73e8' },
  additionalContent: {
    marginTop: 28,
    padding: 20,
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
  footer: {
    marginTop: 40,
    paddingTop: 20,
    borderTop: '1px solid #e0e0e0',
    fontSize: 13,
    color: '#888',
  },
  loading: {
    textAlign: 'center',
    padding: '80px 24px',
    fontSize: 16,
    color: '#666',
  },
  error: {
    textAlign: 'center',
    padding: '80px 24px',
    fontSize: 16,
    color: '#999',
  },
}

const DEFAULT_MESSAGE_TYPES =
  'You may receive messages related to service notifications, appointment reminders, billing and payment updates, and other account-related communications.'
const DEFAULT_MESSAGE_FREQUENCY =
  'Message frequency varies based on your account activity and service schedule.'

const SmsPrivacyPolicyPage = () => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const bid = params.get('bid')

  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!bid) {
      setError('No business specified')
      setLoading(false)
      return
    }

    const apiBase = process.env.REACT_APP_PORTAL_API_BASE_URL || ''
    fetch(`${apiBase}/api/v1/portal/sms-privacy-policy-config?bid=${encodeURIComponent(bid)}`)
      .then(res => {
        if (!res.ok) throw new Error('Privacy policy not found')
        return res.json()
      })
      .then(data => {
        setConfig(data)
        setLoading(false)
        if (data.companyName) {
          document.title = `${data.companyName} - SMS Privacy Policy`
        }
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [bid])

  if (loading) {
    return (
      <div style={styles.body}>
        <div style={styles.loading}>Loading...</div>
      </div>
    )
  }

  if (error || !config) {
    return (
      <div style={styles.body}>
        <div style={styles.error}>
          <p>This privacy policy page is not available.</p>
        </div>
      </div>
    )
  }

  const { companyName, contactEmail, contactPhone, privacyPolicyUrl, messageTypes, messageFrequency, additionalContent } =
    config

  return (
    <div style={styles.body}>
      <div style={styles.container}>
        <h1 style={styles.h1}>{companyName} SMS Privacy Policy</h1>
        <p style={styles.subtitle}>SMS/Text Messaging Terms and Privacy</p>

        <h2 style={styles.h2}>What is this?</h2>
        <p style={styles.p}>
          {companyName} uses SMS text messaging to communicate with our customers. By providing your phone number and
          opting in to receive text messages, you consent to receive messages from {companyName}.
        </p>

        <h2 style={styles.h2}>Types of Messages</h2>
        <p style={styles.p}>{messageTypes || DEFAULT_MESSAGE_TYPES}</p>

        <h2 style={styles.h2}>Message Frequency</h2>
        <p style={styles.p}>{messageFrequency || DEFAULT_MESSAGE_FREQUENCY}</p>

        <h2 style={styles.h2}>Costs</h2>
        <p style={styles.p}>
          Message and data rates may apply. Check with your mobile carrier for details about your messaging plan.
        </p>

        <h2 style={styles.h2}>Opt-Out</h2>
        <p style={styles.p}>
          You can opt out of receiving SMS messages at any time by replying <span style={styles.keyword}>STOP</span> to
          any message you receive from us. After opting out, you will receive a one-time confirmation message and will no
          longer receive text messages from {companyName}.
        </p>

        <h2 style={styles.h2}>Help</h2>
        <p style={styles.p}>
          For help or questions about our SMS messaging, reply <span style={styles.keyword}>HELP</span> to any message
          you receive from us, or contact us directly:
        </p>
        <ul>
          {contactEmail && (
            <li style={styles.p}>
              Email: <a style={styles.link} href={`mailto:${contactEmail}`}>{contactEmail}</a>
            </li>
          )}
          {contactPhone && <li style={styles.p}>Phone: {contactPhone}</li>}
        </ul>

        <h2 style={styles.h2}>Privacy</h2>
        <p style={styles.p}>
          We respect your privacy. Your phone number and opt-in data will not be shared with or sold to third parties for
          marketing purposes. Information collected through SMS may be used to deliver the messages you&apos;ve requested
          and to improve our services.
        </p>
        {privacyPolicyUrl && (
          <p style={styles.p}>
            For our full privacy policy, visit:{' '}
            <a style={styles.link} href={privacyPolicyUrl}>{privacyPolicyUrl}</a>
          </p>
        )}

        <h2 style={styles.h2}>Supported Carriers</h2>
        <p style={styles.p}>
          SMS messaging is supported on most major US carriers. Carriers are not liable for delayed or undelivered
          messages.
        </p>

        {additionalContent && (
          <div style={styles.additionalContent}>
            <p style={styles.p}>{additionalContent}</p>
          </div>
        )}

        <div style={styles.footer}>
          <p>&copy; {companyName}. All rights reserved.</p>
        </div>
      </div>
    </div>
  )
}

export default SmsPrivacyPolicyPage
